/**
 * Session Tracking Hook
 * Tracks when authenticated users start a new session (return to the site)
 * Fires 'session_start' event to ClickHouse once per session
 */

import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { isAnalyticsDisabled } from '../utils/analytics-enabled';
import { getRankingVariant } from '../utils/ranking-variant-store';
import { getAnonymousId, getSessionId } from '../utils/tracking-context';

export function useSessionTracking() {
  const { data: session, status } = useSession();

  useEffect(() => {
    if (isAnalyticsDisabled()) return;

    // Only track if user is authenticated
    if (status !== 'authenticated' || !session?.user?.id) {
      return;
    }

    // Read through tracking-context, not raw storage. A bare getItem misses the
    // three things the accessor does: it slides the idle timeout so an active
    // visit does not expire mid-flow, it mirrors the id into the cookie the
    // server reads, and it MINTS one when none exists. Reading raw meant a
    // first page load -- or any browser with storage blocked -- returned null
    // here and `session_start` simply never fired for that visit.
    const sessionId = getSessionId();
    const anonymousId = getAnonymousId();

    if (!sessionId || !anonymousId) {
      return;
    }

    // Check if we've already tracked session_start for this session
    const trackedKey = `sw_session_tracked_${sessionId}`;
    const hasTracked = sessionStorage.getItem(trackedKey);

    if (hasTracked) {
      // Already tracked this session
      return;
    }

    // Track session start
    const trackSessionStart = async () => {
      try {
        const environment =
          process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
            ? 'staging'
            // Next always sets NODE_ENV.
            : (process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.NODE_ENV);

        const authEvent = {
          event_type: 'session_start',
          user_id: parseInt(String(session.user?.id)),
          email: session.user.email || undefined,
          session_id: sessionId,
          anonymous_id: anonymousId,
          auth_method: session.provider || 'unknown',
          page_url: window.location.href,
          referrer: document.referrer || '',
          user_agent: navigator.userAgent,
          ip_address: '0.0.0.0', // Will be set server-side
          // Which ranking arm served this visit. session_start is a visitor row
          // like any other; without the arm it cannot be split in experiment
          // dashboards, which reads identically to "no traffic yet".
          ranking_variant: getRankingVariant(),
          environment,
          timestamp: new Date().toISOString(),
        };

        await fetch('/api/analytics/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ events: [authEvent] }),
        });

        // Mark this session as tracked
        sessionStorage.setItem(trackedKey, 'true');
        console.log('[SessionTracking] Tracked session_start for user:', session.user?.id);
      } catch (error) {
        console.error('[SessionTracking] Failed to track session_start:', error);
      }
    };

    void trackSessionStart();
  }, [status, session]);
}
